import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './leaderboard.css';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { apiUrl } from '../../utils/apiUrl';
import { useMobileAppBrowserBackGuard } from '../../utils/useMobileAppBrowserBack';

const S3 = process.env.REACT_APP_S3_PATH || '';

function Leaderboard() {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);

  const [matches, setMatches] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackDone, setFeedbackDone] = useState(
    () => !!location.state?.feedbackSubmitted
  );
  const isFromMobileApp =
    new URLSearchParams(location.search).get('fromMobileApp') === 'true';
  const feedbackPath = isFromMobileApp ? '/feedback?fromMobileApp=true' : '/feedback';
  useMobileAppBrowserBackGuard();

  useEffect(() => {
    if (!user || !adminToken) return;
    let cancelled = false;

    (async () => {
      try {
        const resp = await fetch(apiUrl('/gameMatchUsers'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const data = await resp.json();
        if (!cancelled && data.success) {
          setMatches(data.matches || []);
          setCurrentUser(data.currentUser || null);
        }
      } catch (e) {
        console.error('gameMatchUsers', e);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user, adminToken]);

  useEffect(() => {
    if (!user || !adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: user.userId }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw != null && String(raw).trim() !== '') {
          setFeedbackDone(true);
        }
      } catch (e) {
        console.error('spotGameStatus (leaderboard)', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  const gifSrc = themeData?.thank_you_page
    ? S3 + themeData.thank_you_page
    : '/thankyou.gif';

  const heading =
    themeData?.custom_text_thank_you_page || 'Thank you for playing!';
  const btnBg = themeData?.button_backgroundcolor || '#034b82';
  const btnText = themeData?.button_color || '#ffffff';

  const medals = ['🥇', '🥈', '🥉'];
  const barColors = ['#4caf50', '#f59e0b', '#94a3b8', '#cd7f32'];
  const themeTextColorStyle = { ...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.button_color })};

  /** Never list the signed-in user — only other players vs your answers */
  const displayMatches = matches.filter((m) => !m.isSelf);

  return (
    <div className="lb-page">
      <div className="lb-inner">
        {/* <img src={gifSrc} className="lb-gif" alt="" /> */}

        {/* <h2 className="lb-heading" style={{ ...(themeData?.text_color && { color: themeData.text_color }) }}>
          {heading}
        </h2> */}

        <div className="lb-card"  style={{
                ...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }),
                ...(themeData?.button_color && { color: themeData.button_color }),
              }}>
          <h3 className="lb-title" >
            Your Taste Twins
          </h3>
          <p className="lb-subtitle" style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.button_color })}}>
            Top players whose choices match yours the most
          </p>

          {loading ? (
            <div className="lb-loading">
              <div className="lb-spinner" />
              <span>Finding your taste twins…</span>
            </div>
          ) : displayMatches.length === 0 ? (
            <div className="lb-empty" style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.button_color })}}>
              <p>No other players in this session yet — check back after more people play.</p>
            </div>
          ) : (
            <table className="lb-table">
              <colgroup>
                <col className="lb-col-rank" />
                <col className="lb-col-pct" />
                <col className="lb-col-name" />
              </colgroup>
              <thead>
                <tr style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.button_color })}}>
                  <th className="lb-th-rank" scope="col" style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.text_color })}}>
                    Rank
                  </th>
                  <th className="lb-th-pct" scope="col" style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.text_color && { color: themeData.text_color })}}>
                    Match %
                  </th>
                  <th className="lb-th-name" scope="col" style={{...(themeData?.button_backgroundcolor && { backgroundColor: themeData.button_backgroundcolor }), ...(themeData?.button_color && { color: themeData.text_color })}}>
                    Player
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayMatches.map((m, i) => {
                  const rankLabel = medals[i] || `#${i + 1}`;

                  return (
                  <tr
                    key={m.userId || i}
                    className="lb-row"
                  >
                    <td className="lb-td-rank">
                      <span className="lb-medal">{rankLabel}</span>
                    </td>
                    <td className="lb-td-pct">
                      <div className="lb-pct-wrap">
                        <div className="lb-pct-bar">
                          <div
                            className="lb-pct-fill"
                            style={{
                              width: `${m.matchPercent}%`,
                              backgroundColor: barColors[i] || btnBg,
                            }}
                          />
                        </div>
                        <span
                          className="lb-pct-num"
                          style={{ color: themeData.text_color }}
                        >
                          {m.matchPercent}%
                        </span>
                      </div>
                    </td>
                    <td className="lb-td-name"  style={{ color: themeData.text_color }}>
                      {m.name}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          
        </div>

        <div className="lb-feedback-wrap">
          {feedbackDone ? (
            <div
              className="lb-feedback-done"
              style={{
                backgroundColor: btnBg,
                color: btnText,
              }}
            >
              Thanks — you’ve submitted feedback for this session.
            </div>
          ) : (
            <Link
              to={feedbackPath}
              replace
              className="lb-feedback-link"
              style={{
                backgroundColor: btnBg,
                color: btnText,
              }}
            >
              Was this a perfect mix? Share feedback
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
